import { usePortalAuth } from '@nrx/auth/react';
import type { JSX, ReactNode } from 'react';

interface PortalLayoutProps {
  children: ReactNode;
}

/**
 * PortalLayout — wraps authenticated routes with a minimal header.
 *
 * The Sign Out button calls auth.signoutRedirect() which:
 * 1. Redirects the browser to the Cognito Hosted UI logout endpoint
 * 2. The Hosted UI clears the Cognito session cookie
 * 3. Cognito redirects back to post_logout_redirect_uri (/auth/logout)
 * 4. oidc-client-ts clears the in-memory user state
 *
 * This satisfies AC #6: user action → signoutRedirect() → Hosted UI logout →
 * session cleared → landing page.
 */
export function PortalLayout({ children }: PortalLayoutProps): JSX.Element {
  const auth = usePortalAuth();

  function handleSignOut(): void {
    void auth.signoutRedirect();
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-gray-900">NourishedRx Provider Portal</span>
          <button
            onClick={handleSignOut}
            className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
            aria-label="Sign out"
          >
            Sign Out
          </button>
        </div>
      </header>
      <main className="p-6">{children}</main>
    </div>
  );
}
